import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Polygon } from 'geojson';
import {
  API,
  ClientCullDTO,
  CullCountDTO,
  CullDTO,
  CullWithAtomsDTO,
  Entity,
  GeoCullDTO,
  InventoryGeoAggregationFrontendEnhancedDTO,
  PlayerCullDTO,
  PolygonDTO,
} from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { SbModalState } from 'src/app/services/gui/modal/modal-state.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UtilService } from 'src/app/services/util.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { MinMaxPoints } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { UserService } from '../../../services/user.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationService } from '@ngx-seatback/utils';

@Component({
  selector: 'sb-page-filter-details',
  templateUrl: './filter-details.component.html',
  styleUrls: ['./filter-details.component.scss'],
  standalone: false,
})
export class FilterDetailsComponent implements OnInit {
  public GEOCULLDONE: boolean = false;

  typeTranslations = {
    All: this.translate.instant('lang.culls.culltype.all'),
    TimeCull: this.translate.instant('lang.culls.culltype.time'),
    ClientCull: this.translate.instant('lang.culls.culltype.client'),
    ContentCull: this.translate.instant('lang.culls.culltype.content'),
    PlayerCull: this.translate.instant('lang.culls.culltype.player'),
    GeoCull: this.translate.instant('lang.culls.culltype.geo'),
  };

  loading = true;

  @Input() cullId = undefined;

  @Input() hidden = false;
  @Input() type: string = null;
  @Input() isInModalContext: any = null;
  @Input() isInSidebarContext = false;

  companyId?: number;
  showpreview: boolean = true;
  expressionValid: boolean = false;
  easyMode: boolean = true;
  minMaxPoints: MinMaxPoints;
  displayedPolygons: Polygon[] = [];
  geoAggregation: InventoryGeoAggregationFrontendEnhancedDTO;
  // tslint:disable-next-line:variable-name
  _cull: CullWithAtomsDTO;
  showChainedCulls: boolean = false;
  chainedCulls: CullDTO[] = [];

  get cull(): CullWithAtomsDTO {
    return this._cull;
  }

  @Input()
  set cull(value: CullWithAtomsDTO) {
    this._cull = null;
    this._cull = value;
    if (this._cull) {
      this.expressionValid = this.isCullExpressionValid();
    } else {
      this.expressionValid = false;
    }

    this.showpreview = false; // TODO Angular is shitty and change detection is buggy

    this.previewCull = this._cull; // TODO preview call update when culls are updated

    setTimeout(() => {
      this.showpreview = true; // TODO Angular is shitty and change detection is buggy
    }, 100);
  }

  // tslint:disable-next-line:variable-name
  _previewCull: CullWithAtomsDTO;
  protected usages: CullCountDTO[] = [];

  get previewCull(): CullWithAtomsDTO {
    return this._previewCull;
  }

  set previewCull(value: CullWithAtomsDTO) {
    this._previewCull = value;
  }

  get previewComponent() {
    if (this.cull?.type === 'ClientCull') {
      return (this.cull as ClientCullDTO)?.staticElements?.length > 0 || (this.previewCull?.atoms?.length && this.cull?.expression);
    }
    if (this.cull?.type === 'PlayerCull') {
      return (this.cull as PlayerCullDTO)?.staticElements?.length > 0 || (this.previewCull?.atoms?.length && this.cull?.expression);
    }
    return this.previewCull?.atoms?.length && this.cull?.expression;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    private modalState: SbModalState,
    public util: UtilService,
    public translate: TranslateService,
    private userService: UserService,
    private modalNavigation: ModalNavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef,
    private readonly navService: NavigationService
  ) {
    this._cull = null;
  }

  ngOnInit(): void {
    this.translate.onDefaultLangChange.subscribe(() => {
      this.initTranslations();
    });
    this.initTranslations();
    this.loading = true;
    let payload: any;
    if (this.isInModalContext) {
      if (this.modalState.options.get(this.isInModalContext) && this.modalState.options.get(this.isInModalContext).payload) {
        payload = this.modalState.options.get(this.isInModalContext).payload;
      }
      if (this.modalNavigation.getCurrentNavigation().data?.cullId) {
        payload = this.modalNavigation.getCurrentNavigation().data.cullId;
      }
    }
    if (payload) {
      this.cullId = payload;
      this.refreshFromServer();
    } else if (this.cullId) {
      this.refreshFromServer();
    } else {
      this.activatedRoute.params.subscribe(params => {
        this.cullId = params.filterID;
        if (isNaN(this.cullId)) {
          this.cullId = undefined;
        }
        this.refreshFromServer();
      });
    }
  }

  initTranslations() {
    this.typeTranslations = {
      All: this.translate.instant('lang.culls.culltypelong.all'),
      TimeCull: this.translate.instant('lang.culls.culltypelong.time'),
      ClientCull: this.translate.instant('lang.culls.culltypelong.client'),
      ContentCull: this.translate.instant('lang.culls.culltypelong.content'),
      PlayerCull: this.translate.instant('lang.culls.culltypelong.player'),
      GeoCull: this.translate.instant('lang.culls.culltypelong.geo'),
    };
  }

  refreshFromServer() {
    if (!this.cullId) {
      this.cull = new CullWithAtomsDTO();
      if (this.type) {
        this.cull.type = this.type;
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_time_create])) {
        this.cull.type = 'TimeCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_player_create])) {
        this.cull.type = 'PlayerCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_content_create])) {
        this.cull.type = 'ContentCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_client_create])) {
        this.cull.type = 'ClientCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_geo_create]) && this.GEOCULLDONE) {
        this.cull.type = 'GeoCull';
      }
      this.cull.hidden = this.hidden;
      this.cull.easyMode = true;
      this.refreshEasyMode();
      this.loading = false;
    } else {
      this.api
        .cs_Cull_cullUsageCount(this.cullId)
        .pipe(
          switchMap(usages => {
            const chainedItems = usages.filter(usage => usage.type?.startsWith('Chained'));
            const chainedSum = chainedItems.reduce((sum, usage) => sum + (usage.count ?? 0), 0);
            const newUsages = usages.filter(usage => !usage.type?.startsWith('Chained'));
            const chainedSummary = new CullCountDTO();
            chainedSummary.type = Entity.ChainedCullAtom;
            chainedSummary.count = chainedSum;

            newUsages.push(chainedSummary);

            this.usages = newUsages;
            this.chainedCullLookup(chainedItems);

            return this.api.cs_Cull_retrieveById(this.cullId);
          }),
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (cullWithAtoms: CullWithAtomsDTO) => {
            this.cull = cullWithAtoms;
            if (this.cull.type === 'GeoCull') {
              this.getPolygons().then((result: Polygon[]) => {
                this.displayedPolygons = result;
              });
            }
            this.refreshEasyMode();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  chainedCullLookup(cullCounts: CullCountDTO[]) {
    if (!cullCounts || cullCounts.length === 0) {
      this.showChainedCulls = false;
      return;
    }

    const chainedCullCount = cullCounts.find(count => count.count > 0);

    if (!chainedCullCount) {
      this.showChainedCulls = false;
      return;
    }

    this.api
      .cs_Cull_cullUsagesForType(this.cullId, chainedCullCount.type)
      .pipe(
        take(1),
        switchMap((chained: any[]) => {
          const cullIds = chained.map(cull => cull?.id?.cull?.id);
          return this.api.cs_Cull_retrieveAll({ id: cullIds });
        }),
        finalize(() => {
          this.showChainedCulls = true;
        })
      )
      .subscribe({
        next: culls => {
          this.chainedCulls = culls;
        },
      });
  }

  refreshEasyMode() {
    this.easyMode = this.cull.easyMode;
  }

  isCullExpressionValid() {
    return true;
  }

  filterInventoryMap() {
    this.api
      .ds_rm_Inventory_geoAggregationFrontendEnhanced(
        0,
        {
          filters: [],
          whitelistPolygons: null,
          blacklistPolygons: null,
          playerCulls: this.cull ? [this.cull as PlayerCullDTO] : null,
          geoCulls: null,
          descriptorId: null,
        },
        { min: this.minMaxPoints.min, max: this.minMaxPoints.max }
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((aggregation: InventoryGeoAggregationFrontendEnhancedDTO) => {
        this.geoAggregation = aggregation;
      });
  }

  refreshMap(point?: MinMaxPoints) {
    if (point) {
      this.minMaxPoints = point;
    }
    this.filterInventoryMap();
  }

  hasEditPermission() {
    if (this.cull?.global) {
      return false;
    }
    if (this.cull?.lockedBy) {
      return false;
    }
    return this.hasEditRight();
  }

  hasEditRight() {
    switch (this.cull?.type) {
      case 'TimeCull':
        return this.permissionService.hasPermission(this.permissionService.Permission.cull_time_edit);
      case 'PlayerCull':
        return this.permissionService.hasPermission(this.permissionService.Permission.cull_player_edit);
      case 'ClientCull':
        return this.permissionService.hasPermission(this.permissionService.Permission.cull_client_edit);
      case 'GeoCull':
        return this.permissionService.hasPermission(this.permissionService.Permission.cull_geo_edit);
      case 'ContentCull':
        return this.permissionService.hasPermission(this.permissionService.Permission.cull_content_edit);
      default:
        return false;
    }
  }

  getPolygons(): Promise<Polygon[]> {
    return new Promise<Polygon[]>(resolve => {
      const geoCull = this.cull as GeoCullDTO;

      if (geoCull.validPolygons.length > 0) {
        for (const poly of geoCull.validPolygons) {
          this.util.geo.checkInCachedPolygon(poly);
        }
        this.util.geo.getCachedPolygons(this.util.arrayToIdArray(geoCull.validPolygons)).then((cachedPolygons: PolygonDTO[]) => {
          if (cachedPolygons) {
            resolve(this.util.geo.polygonDTOArrayToPolygonArray(cachedPolygons));
          } else {
            resolve([]);
          }
        });
      } else {
        resolve([]);
      }
    });
  }

  toggleHideCull() {
    if (!this.cull) {
      return;
    }
    this.cull.hidden = !this.cull.hidden;
    this.api
      .cs_Cull_setHidden(this.cull.id, { hidden: this.cull.hidden })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {},
      });
  }

  toggleLockCull() {
    this.loading = true;
    if (!this.cull) {
      return;
    }
    const lockStatus: boolean = !this.cull.lockedBy;
    if (this.permissionService.hasPermission(this.permissionService.Permission.cull_lock_admin)) {
      this.api
        .cs_Cull_setLockAdmin(this.cull.id, { unlock: !lockStatus })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            this.refreshFromServer();
          },
        });
    } else {
      this.api
        .cs_Cull_setLock(this.cull.id, { unlock: !lockStatus })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            this.refreshFromServer();
          },
        });
    }
  }

  isLockDisabled(): boolean {
    if (!this.cull) {
      return true;
    }
    if (this.cull?.global) {
      return true;
    }
    if (this.permissionService.hasPermission(this.permissionService.Permission.cull_lock_admin)) {
      return false;
    }
    if (!this.cull.lockedBy) {
      return false;
    }
    return !(this.cull.lockedBy && this.cull.lockedBy?.id === this.userService.user?.user?.id);
  }

  protected navigateToCull(id: any) {
    this.navService
      .navigateToPoint(this.util.RoutingID.filterDetails, [{ id: 'filterID', value: id }])
      .then(() => window.location.reload());
  }
}
